import { apiClient } from './client';

/**
 * 예산 편성 (2026-09-04 · 예산 판 B2).
 *
 * ★★★ **잔액과 가용은 다르다.** 잔액 = 편성 − 집행, 가용 = 잔액 − **신청중**.
 *    화면이 크게 보여 줄 것은 **가용**이다 — 잔액만 보면 결재가 도는 돈을 또 쓴다.
 * ★ 옛 서버는 이 길이 없다(404) — 화면이 「아직 없습니다」로 접는다.
 */

export interface BudgetPeriodDto {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status: 'DRAFT' | 'OPEN' | 'CLOSED' | string;
  lineCount: number;
  createdAt: string;
}

/** 편성 − 집행 = 잔액 · 잔액 − 신청중 = 가용. 서버가 한 규칙으로 센 값이다. */
export interface BudgetAmounts {
  planned: number;
  spent: number;
  pending: number;
  remaining: number;
  available: number;
}

export interface BudgetLineDto extends BudgetAmounts {
  id: string;
  orgKind: string;
  orgId: string | null;
  accountCode: string;
  memo: string | null;
  entryCount: number;
}

export interface BudgetEntryDto {
  id: string;
  kind: string;
  amount: number;
  reason: string | null;
  by: string | null;
  at: string;
}

export async function listBudgetPeriods(): Promise<BudgetPeriodDto[]> {
  const { data } = await apiClient.get<{ periods: BudgetPeriodDto[] }>('/budget/periods');
  return data.periods ?? [];
}

export async function createBudgetPeriod(body: {
  name: string; startDate: string; endDate: string;
}): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>('/budget/periods', body);
  return data;
}

export async function patchBudgetPeriod(id: string, body: Record<string, string>): Promise<void> {
  await apiClient.patch(`/budget/periods/${id}`, body);
}

export async function listBudgetLines(periodId: string): Promise<{
  period: { id: string; name: string; startDate: string; endDate: string; status: string };
  lines: BudgetLineDto[];
  total: BudgetAmounts;
}> {
  const { data } = await apiClient.get(`/budget/periods/${periodId}/lines`);
  return data as never;
}

export async function createBudgetLine(periodId: string, body: {
  orgKind?: string; orgId?: string | null; accountCode: string; memo?: string | null; amount?: number;
}): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>(`/budget/periods/${periodId}/lines`, body);
  return data;
}

export async function deleteBudgetLine(id: string): Promise<void> {
  await apiClient.delete(`/budget/lines/${id}`);
}

export async function listBudgetEntries(lineId: string): Promise<{
  accountCode: string; entries: BudgetEntryDto[];
} & BudgetAmounts> {
  const { data } = await apiClient.get(`/budget/lines/${lineId}/entries`);
  return data as never;
}

export async function addBudgetEntry(lineId: string, body: {
  kind: string; amount: number; reason?: string | null;
}): Promise<void> {
  await apiClient.post(`/budget/lines/${lineId}/entries`, body);
}

/**
 * 기안하기 **전에** 예산을 묻는다 (2026-09-04 · 판 B4).
 *
 * ★★★ **예산 총액·잔액은 안 온다** — 「넘는 만큼」만 온다. 예산 화면은 담당자 전용이고,
 *    기안은 누구나 하므로 그 담을 넘지 않으려는 것이다.
 * ★★ **아무것도 막지 않는다**(결정 B5-a) — 넘는다고 말할 뿐이고 올릴지는 사람이 정한다.
 * ★ 옛 서버는 404 다 → 부르는 쪽이 조용히 접는다(기안을 막지 않는다).
 */
export interface BudgetPreviewLineDto {
  seq: number;
  /** 어느 예산에 붙나 — 「2026 회계연도 · 전사」. 못 붙으면 `null`. */
  budgetName: string | null;
  scope: 'ORG' | 'COMPANY' | null;
  /** 못 붙은 이유(사람 말). 붙었으면 `null`. */
  miss: string | null;
  /** **넘는 만큼** — 0 이면 안 넘는다. 잔액이 아니다. */
  over: number;
}

export async function previewBudget(body: {
  dept?: string | null;
  lines: Array<{
    seq: number; spentOn?: string | null; accountCode?: string | null;
    accountLabel?: string | null; amount: number;
  }>;
}): Promise<BudgetPreviewLineDto[]> {
  const { data } = await apiClient.post<{ lines: BudgetPreviewLineDto[] }>('/budget/preview', body);
  return data.lines ?? [];
}
